import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';

import {
  useAdminUser,
  useAdminUserAuthEvents,
  type AdminUserAuthEventRow,
  type AdminUserDetail,
  type AuthEventOutcome,
  type AuthRiskFlag,
} from './api';
import { DASH, formatTimestamp, timeAgo } from './shared';
import { connectedAgentLabel, signupOriginLabel } from './identity';

const RISK_FLAG_LABELS: Record<AuthRiskFlag, string> = {
  many_failures: 'Many failures (24h)',
  success_after_failures: 'Success after failures',
  no_successful_login: 'No successful login',
};

const OUTCOME_BADGE: Record<AuthEventOutcome, 'default' | 'destructive' | 'outline'> = {
  success: 'default',
  failure: 'destructive',
  blocked: 'destructive',
  unknown: 'outline',
};

interface UserDrawerProps {
  userId: string | null;
  onClose: () => void;
}

export function UserDrawer({ userId, onClose }: UserDrawerProps) {
  const { data, isLoading, error } = useAdminUser(userId);
  const open = !!userId;

  // Cross-tab navigation helpers
  const [, setSearchParams] = useSearchParams();
  const goTab = (tab: string, id?: string) => {
    const next = new URLSearchParams();
    next.set('tab', tab);
    if (id) next.set('id', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="flex flex-col gap-0">
        <SheetHeader className="space-y-1">
          <SheetTitle>{data?.email ?? 'User'}</SheetTitle>
          <SheetDescription>
            {userId ? (
              <span className="text-xs">
                Fantopy user ID <span className="font-mono">{userId}</span>
              </span>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load'}
          </div>
        ) : data ? (
          <div className="space-y-6 py-4">
            <Section title="Identity">
              <Row label="Email" value={data.email ?? DASH} />
              <Row label="Handle" value={data.prediction_handle ?? DASH} />
              <Row label="Manager name" value={data.agent_name ?? DASH} />
              <Row
                label="Supabase UID"
                value={<code className="text-xs">{data.supabase_uid}</code>}
              />
              <Row
                label="Email verified"
                value={
                  (data.email_verified ?? data.verified_human) ? (
                    <Badge>Verified</Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )
                }
              />
              <Row label="Signup origin" value={signupOriginLabel(data.created_via)} />
              <Row label="Connected agent" value={connectedAgentLabel(data.connected_agent_count)} />
              <Row label="First recorded auth" value={data.first_auth_provider ?? DASH} />
              <Row label="Created at" value={formatTimestamp(data.created_at)} />
              <Row label="Updated at" value={timeAgo(data.updated_at)} />
            </Section>

            <Section
              title={`Onboarding sessions (${data.onboarding_sessions.length})`}
              empty={data.onboarding_sessions.length === 0}
            >
              {data.onboarding_sessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/40"
                  onClick={() => goTab('onboarding-sessions', s.id)}
                >
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{s.status}</Badge>
                    <span className="font-mono text-xs">{s.id.slice(0, 8)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {timeAgo(s.last_activity_at)}
                  </span>
                </button>
              ))}
            </Section>

            <Section title={`Chat threads (${data.chat_threads.length})`} empty={data.chat_threads.length === 0}>
              {data.chat_threads.map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/40"
                  onClick={() => goTab('chat-logs', thread.id)}
                >
                  <span className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <Badge variant="outline">{thread.kind}</Badge>
                      <span className="font-medium">
                        {thread.agent_name ?? formatChatThreadLabel(thread.kind)}
                      </span>
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {thread.game_key}
                    </span>
                  </span>
                  <span className="text-right text-xs text-muted-foreground">
                    <span className="block">{thread.message_count} messages</span>
                    <span>{timeAgo(thread.last_activity_at)}</span>
                  </span>
                </button>
              ))}
            </Section>

            <Section title={`Team records (${data.agents.length})`} empty={data.agents.length === 0}>
              {data.agents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/40"
                  onClick={() => goTab('agents', a.id)}
                >
                  <span className="flex flex-col">
                    <span className="font-medium">{a.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {a.wallet_address.slice(0, 6)}…{a.wallet_address.slice(-4)}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{a.entry_count} entries</span>
                    <span>{a.feedback_count} feedback</span>
                  </span>
                </button>
              ))}
            </Section>

            <Section
              title={`Competition entries (${data.entries.length})`}
              empty={data.entries.length === 0}
            >
              {data.entries.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{e.competition_format}</Badge>
                    <span className="font-medium">{e.competition_title}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(e.created_at)}
                  </span>
                </div>
              ))}
            </Section>

            <AuthHistorySection userId={data.id} auth={data.auth} />

            <Section title="Activity counts">
              <Row label="Feedback submitted" value={data.feedback_count} />
              <Row label="Chat threads" value={data.chat_threads.length} />
            </Section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function formatChatThreadLabel(kind: string): string {
  if (kind === 'onboarding') return 'Onboarding conversation';
  if (kind === 'manager') return 'Manager conversation';
  return 'Conversation';
}

/**
 * Auth history (issue #371) — replaces the reliance on derived `Last
 * active`. Top: the aggregate summary + risk flags. Below: a filterable,
 * paginated chronological timeline from `/users/:id/auth-events`, which
 * also surfaces email-matched attempts that never bound to this row.
 */
function AuthHistorySection({
  userId,
  auth,
}: {
  userId: string;
  auth: AdminUserDetail['auth'];
}) {
  const [outcome, setOutcome] = useState<AuthEventOutcome | 'any'>('any');
  const [offset, setOffset] = useState(0);
  const PAGE = 20;
  const { data, isLoading, error } = useAdminUserAuthEvents(userId, {
    outcome,
    limit: PAGE,
    offset,
  });
  const events = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Auth history
      </h3>

      <div className="space-y-2">
        <Row label="Last login" value={timeAgo(auth.last_login_at)} />
        <Row
          label="Login method"
          value={auth.last_login_method ? <Badge variant="outline">{auth.last_login_method}</Badge> : DASH}
        />
        <Row label="Last failed login" value={timeAgo(auth.last_failed_login_at)} />
        <Row
          label="Failures / successes (24h)"
          value={
            <span>
              <span className={auth.failures_24h > 0 ? 'text-destructive' : undefined}>
                {auth.failures_24h}
              </span>{' '}
              / {auth.successes_24h}
            </span>
          }
        />
        <Row label="Distinct sources (30d)" value={auth.distinct_recent_ips} />
        {auth.risk_flags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {auth.risk_flags.map((flag) => (
              <Badge key={flag} variant="destructive">
                {RISK_FLAG_LABELS[flag] ?? flag}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Timeline</span>
        <select
          className="rounded-md border bg-background px-2 py-1 text-xs"
          value={outcome}
          onChange={(e) => {
            setOutcome(e.target.value as AuthEventOutcome | 'any');
            setOffset(0);
          }}
        >
          <option value="any">All outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="blocked">Blocked</option>
          <option value="unknown">Unknown</option>
        </select>
      </div>

      <div className="mt-2 space-y-1.5">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load auth history'}
          </p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No auth events recorded.</p>
        ) : (
          events.map((ev) => <AuthEventRow key={ev.id} ev={ev} ownUserId={userId} />)
        )}
      </div>

      {total > PAGE ? (
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <button
            type="button"
            className="rounded-md border px-2 py-1 disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Prev
          </button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <button
            type="button"
            className="rounded-md border px-2 py-1 disabled:opacity-40"
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AuthEventRow({ ev, ownUserId }: { ev: AdminUserAuthEventRow; ownUserId: string }) {
  // An event that matched this user only by email — never bound to their
  // `users` row. Worth flagging: it's an attempt against their address.
  const emailOnlyMatch = ev.user_id == null || ev.user_id !== ownUserId;
  return (
    <div className="rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <Badge variant={OUTCOME_BADGE[ev.outcome] ?? 'outline'}>{ev.outcome}</Badge>
          <span className="font-medium">{ev.action}</span>
          {ev.provider ? (
            <span className="text-xs text-muted-foreground">{ev.provider}</span>
          ) : null}
        </span>
        <span className="text-xs text-muted-foreground">{formatTimestamp(ev.occurred_at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        {ev.reason_code ? <span>reason: {ev.reason_code}</span> : null}
        {ev.country ? <span>country: {ev.country}</span> : null}
        {ev.ip_hash ? <span>src: {ev.ip_hash.slice(0, 10)}…</span> : null}
        {emailOnlyMatch ? (
          <span className="text-amber-600 dark:text-amber-500">email-only match</span>
        ) : null}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  empty,
}: {
  title: string;
  children: React.ReactNode;
  empty?: boolean;
}) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {empty ? (
        <p className="text-sm text-muted-foreground">{DASH}</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
